import moment from 'moment'
import { EnumCouponType, EnumCouponValidTimeType } from '@xt/client/enums'
import { BaseCouponItem } from '../../types/coupon'

/**
 * 当前券的时间是否符合前端展示永久有效券
 * @param couponItem 优惠券时间参数信息
 */
export function isPermanentTimeCoupon(couponItem: Pick<BaseCouponItem, 'validType' | 'validTimeDuration' | 'validEndTime'>) {
  if (couponItem.validType === EnumCouponValidTimeType.Relative) {
    return couponItem.validTimeDuration === 863913600 /** 863913600 / 24 / 60 / 60 = 9999天 */
  } else {
    return moment(couponItem.validEndTime).format('X') === '32503651200' /** Wed Jan 01 3000 00:00:00 GMT+0800 */
  }
}

function toBeijingTime(timestemp: number): string {
  return moment(timestemp).utcOffset(+8).format('YYYY.MM.DD HH:mm')
}

/**
 * 将时间duration转化为实际的天数
 * @param duration 时间duration
 * @returns 天
 */
function duration2Day(duration: number) {
  return duration / 60 / 60 / 24
}

export enum EnumCouponTimeRule {
  PositionRule1 = 'coupon:time-rule:1',
  PositionRule2 = 'coupon:time-rule:2',
  PositionRule3 = 'coupon:time-rule:3',
  // 出现此项表示位置没有被适配到 需要适配，如果遗漏生成按照PositionRule1展示
  PositionRule0 = 'coupon:time-rule:0'
}

/**
 * 位置
 * - 下单页赠品弹窗
 * - 订单赠品弹窗
 * - 积分兑换
 * - 领取文房券 - 确认领取信息
 *
 * 优惠券 展示时间规则:
 *  - 永久有效
 *    - 绝对时间: 获得后永久有效
 *    - 相对时间: 获得后永久有效
 *  - 非永久有效
 *    - 绝对时间: YYYY:MM.DD HH:MM到期
 *    - 相对时间: 获得后D天内可用
 *
 * 使用信息 展示规则:
 *  - 永久有效
 *    - 绝对时间: YYYY:MM.DD HH:MM - 永久有效
 *    - 相对时间: 永久有效
 *  - 非永久有效
 *    - 绝对时间: YYYY:MM.DD HH:MM - YYYY:MM.DD HH:MM
 *    - 相对时间: 获得后D天内可用
 */
type PositionRule1 = EnumCouponTimeRule.PositionRule1

/**
 * 位置
 * - 订单页 用户已选择使用的优惠券列表
 * - 订单页可使用福利券弹窗
 *
 * 优惠券 展示时间规则:
 *  - 永久有效
 *    - 绝对时间: 永久有效
 *    - 相对时间: 永久有效
 *  - 非永久有效
 *    - 绝对时间: YYYY:MM.DD HH:MM到期
 *    - 相对时间: YYYY:MM.DD HH:MM到期
 *
 * 使用信息 展示规则:
 *  - 永久有效
 *    - 绝对时间: YYYY:MM.DD HH:MM - 永久有效
 *    - 相对时间: 永久有效
 *  - 非永久有效
 *    - 绝对时间: YYYY:MM.DD HH:MM - YYYY:MM.DD HH:MM
 *    - 相对时间: YYYY:MM.DD HH:MM - YYYY:MM.DD HH:MM
 */
type PositionRule2 = EnumCouponTimeRule.PositionRule2

/**
 * 位置
 * - 首页福利券领取弹窗
 * - 下单成功福利券领取弹窗
 * - 我的卡券
 * - 下单页不可使用福利券列表
 * - 优惠券赠送页面
 *
 * 优惠券 展示时间规则:
 *  - !文房券未领取
 *    - 绝对时间: YYYY:MM.DD HH:MM - YYYY:MM.DD HH:MM
 *    - 相对时间: 获得后D天内可用
 *  - 永久有效
 *    - 绝对时间: YYYY:MM.DD HH:MM - 永久有效
 *    - 相对时间: 永久有效
 *  - 非永久有效
 *    - 绝对时间: YYYY:MM.DD HH:MM - YYYY:MM.DD HH:MM
 *    - 相对时间: YYYY:MM.DD HH:MM - YYYY:MM.DD HH:MM
 *
 * 使用信息 展示规则:
 * - !文房券未领取
 *    - 绝对时间: YYYY:MM.DD HH:MM - YYYY:MM.DD HH:MM
 *    - 相对时间: 获得后D天内可用
 *  - 永久有效
 *    - 绝对时间: YYYY:MM.DD HH:MM - 永久有效
 *    - 相对时间: 永久有效
 *  - 非永久有效
 *    - 绝对时间: YYYY:MM.DD HH:MM - YYYY:MM.DD HH:MM
 *    - 相对时间: YYYY:MM.DD HH:MM - YYYY:MM.DD HH:MM
 */
type PositionRule3 = EnumCouponTimeRule.PositionRule3

export type CouponPositionRules = PositionRule1 | PositionRule2 | PositionRule3

type TimeInfoResult = {
  /**
   * 卡片上的时间说明
   */
  card: string
  /**
   * 使用说明详情中的时间说明
   */
  info: string
}

function getCouponTimeInfoRule1(coupon: BaseCouponItem, _rule: PositionRule1 = EnumCouponTimeRule.PositionRule1): TimeInfoResult {
  if (isPermanentTimeCoupon(coupon)) {
    return {
      card: '获得后永久有效',
      info: coupon.validType === EnumCouponValidTimeType.Relative ? '永久有效' : `${toBeijingTime(coupon.validStartTime)} - 永久有效`
    }
  } else {
    return {
      card:
        coupon.validType === EnumCouponValidTimeType.Absolute
          ? `${toBeijingTime(coupon.validEndTime)}到期`
          : `获得后${duration2Day(coupon.validTimeDuration)}天内可用`,
      info:
        coupon.validType === EnumCouponValidTimeType.Absolute
          ? `${toBeijingTime(coupon.validStartTime)} - ${toBeijingTime(coupon.validEndTime)}`
          : `获得后${duration2Day(coupon.validTimeDuration)}天内可用`
    }
  }
}

function getCouponTimeInfoRule2(coupon: BaseCouponItem, _rule: PositionRule2 = EnumCouponTimeRule.PositionRule2): TimeInfoResult {
  if (isPermanentTimeCoupon(coupon)) {
    return {
      card: '永久有效',
      info: coupon.validType === EnumCouponValidTimeType.Absolute ? `${toBeijingTime(coupon.validStartTime)} - 永久有效` : '永久有效'
    }
  } else {
    return {
      card: `${toBeijingTime(coupon.validEndTime)}到期`,
      info: `${toBeijingTime(coupon.validStartTime)} - ${toBeijingTime(coupon.validEndTime)}`
    }
  }
}

function getCouponTimeInfoRule3(coupon: BaseCouponItem, _rule: PositionRule3 = EnumCouponTimeRule.PositionRule3): TimeInfoResult {
  if (isPermanentTimeCoupon(coupon)) {
    return {
      card: coupon.validType === EnumCouponValidTimeType.Relative ? '永久有效' : `${toBeijingTime(coupon.validStartTime)} - 永久有效`,
      info: coupon.validType === EnumCouponValidTimeType.Relative ? '永久有效' : `${toBeijingTime(coupon.validStartTime)} - 永久有效`
    }
  } else {
    // 优先判断未领取的文房券
    if (coupon.couponType === EnumCouponType.PencilStore && coupon.wenfangReceived === false) {
      return {
        card:
          coupon.validType === EnumCouponValidTimeType.Relative
            ? `获得后${duration2Day(coupon.validTimeDuration)}天内可用`
            : `${toBeijingTime(coupon.validStartTime)} - ${toBeijingTime(coupon.validEndTime)}`,
        info:
          coupon.validType === EnumCouponValidTimeType.Relative
            ? `获得后${duration2Day(coupon.validTimeDuration)}天内可用`
            : `${toBeijingTime(coupon.validStartTime)} - ${toBeijingTime(coupon.validEndTime)}`
      }
    } else {
      return {
        card: `${toBeijingTime(coupon.validStartTime)} - ${toBeijingTime(coupon.validEndTime)}`,
        info: `${toBeijingTime(coupon.validStartTime)} - ${toBeijingTime(coupon.validEndTime)}`
      }
    }
  }
}

/**
 * 优惠券根据在不同的位置可能会展示不同的文案
 * @see https://nas.xuantong.cn team_tech_fe/工作记录/备份/卡劵时间处理.20250918.pdf
 */
export function getCouponTimeInfoByPosition(coupon: BaseCouponItem, rule: CouponPositionRules) {
  switch (rule) {
    case EnumCouponTimeRule.PositionRule1:
      return getCouponTimeInfoRule1(coupon)
    case EnumCouponTimeRule.PositionRule2:
      return getCouponTimeInfoRule2(coupon)
    case EnumCouponTimeRule.PositionRule3:
      return getCouponTimeInfoRule3(coupon)
    default:
      return getCouponTimeInfoRule1(coupon)
  }
}
